import React, { useEffect, useState } from 'react';

const getColorStyles = (color) => {
  const isGradient = color && String(color).includes('gradient');

  if (isGradient) {
    return {
      backgroundImage: color,
      WebkitBackgroundClip: 'text',
      backgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    };
  }
  if (color) {
    return { color, WebkitTextFillColor: color };
  }
  // Override any inherited `-webkit-text-fill-color: transparent` from a
  // parent <tg>. background-clip:text doesn't compose reliably with the
  // transformed/clipped odometer subtree, so the ticker renders in the
  // surrounding currentColor instead of the gradient.
  return { WebkitTextFillColor: 'currentColor' };
};

const OdometerDigit = ({ char, duration, color }) => {
  // Compute styles for this digit (handles gradients and fallbacks)
  const colorStyles = getColorStyles(color);

  // If not a number, render normally (e.g. ₹ symbol)
  if (!/^[0-9]$/.test(char)) {
    return (
      <span style={{ display: 'contents' }}>
        <span
          style={{
            ...colorStyles,
            verticalAlign: 'middle',
            height: '1em',
            width: '1ch',
            lineHeight: 1,
          }}
        >
          {char}
        </span>
      </span>
    );
  }

  const digits = ['9', '8', '7', '6', '5', '4', '3', '2', '1', '0'];
  const targetIndex = digits.indexOf(char);

  return (
    <span
      style={{
        display: 'inline-block',
        overflow: 'hidden',
        height: '1em',
        width: '1ch',
        lineHeight: 1,
        verticalAlign: 'middle',
        fontVariantNumeric: 'tabular-nums',
        textAlign: 'center',
        ...colorStyles, // Explicitly apply the color from the API
      }}
    >
      <span
        style={{
          display: 'block',
          transform: `translateY(-${targetIndex}em)`,
          transition: `transform ${duration}ms cubic-bezier(0.2, 0.8, 0.2, 1)`,
        }}
      >
        {digits.map((d) => (
          <span
            key={d}
            style={{
              display: 'block',
              height: '1em',
              lineHeight: '1em',
            }}
          >
            {d}
          </span>
        ))}
      </span>
    </span>
  );
};

export default function VariableRenderer({ id, config = {} }) {
  const value =
    typeof config === 'object' && config !== null ? config.value : config;
  const type =
    typeof config === 'object' && config !== null ? config.type : undefined;
  const props =
    typeof config === 'object' && config !== null ? (config.props ?? {}) : {};

  const duration = props.duration ?? 800;
  const customColor = props.color; // Pulling the color directly from the API!

  const [displayValue, setDisplayValue] = useState(() => {
    if (type !== 'ticker') return String(value ?? `[MISSING: ${id}]`);
    return String(value ?? '').replace(/[0-9]/g, '0');
  });

  useEffect(() => {
    if (type !== 'ticker') return;

    const timer = setTimeout(() => {
      setDisplayValue(String(value ?? ''));
    }, 50);

    return () => clearTimeout(timer);
  }, [value, type]);

  return (
    <span
      data-variable-id={id}
      className="variable"
      style={{ display: 'contents' }}
    >
      {type === 'ticker' ? (
        String(displayValue)
          .split('')
          .map((char, index) => (
            <OdometerDigit
              key={index}
              char={char}
              duration={duration}
              color={customColor}
            />
          ))
      ) : (
        <span style={getColorStyles(customColor)}>{displayValue}</span>
      )}
    </span>
  );
}
